import { ChevronsUpDown } from "lucide-react";
import { memo } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

interface EvalTableShimmerProps {
  columnCount?: number;
  rowCount?: number;
  showControls?: boolean;
  showPagination?: boolean;
  hasHiddenColumns?: boolean;
  className?: string;
}

const EvalTableShimmer = memo(
  ({
    columnCount = 5,
    rowCount = 10,
    showControls = false,
    showPagination = true,
    hasHiddenColumns = false,
    className,
  }: EvalTableShimmerProps) => {
    // Generate column widths for more realistic shimmer
    const columnWidths = Array.from({ length: columnCount }, (_, i) => {
      if (i === 0) {
        return "120px";
      } // First column usually narrower
      return `${Math.floor(Math.random() * 50 + 100)}px`;
    });

    return (
      <div className={cn("flex min-h-0 flex-1 flex-col", className)}>
        {/* Controls shimmer */}
        {showControls && (
          <div className="mb-4 flex flex-shrink-0 flex-wrap items-center gap-2">
            <Skeleton className="h-10 w-64" /> {/* Search bar */}
            <div className="flex gap-2">
              <Skeleton className="h-8 w-20 rounded-full" />
              <Skeleton className="h-8 w-20 rounded-full" />
            </div>
          </div>
        )}

        {/* Table shimmer */}
        <div className="min-h-0 flex-1 overflow-auto rounded-lg border bg-gray-50">
          <div className="rounded-md">
            <Table>
              <TableHeader className="bg-white">
                <TableRow>
                  {/* Hidden columns dropdown shimmer */}
                  {hasHiddenColumns && (
                    <TableHead className="w-[100px]">
                      <Skeleton className="mx-2 h-8 w-20" />
                    </TableHead>
                  )}

                  {/* Column headers shimmer */}
                  {Array.from({ length: columnCount }).map((_, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <TableHead key={index} className="h-auto whitespace-nowrap border-gray-200">
                      <div className="flex items-center space-x-2 px-2 py-2">
                        <Skeleton className="h-4" style={{ width: columnWidths[index] }} />
                        <ChevronsUpDown className="h-4 w-4 animate-pulse text-gray-300" />
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>

              <TableBody>
                {Array.from({ length: rowCount }).map((_, rowIndex) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <TableRow key={rowIndex} className="group">
                    {/* Hidden column cell */}
                    {hasHiddenColumns && <TableCell className="w-[100px]" />}

                    {/* Data cells shimmer */}
                    {Array.from({ length: columnCount }).map((__, cellIndex) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <TableCell key={cellIndex} className="px-4 py-3">
                        <Skeleton
                          className="h-5"
                          style={{
                            width: `${Math.floor(Math.random() * 40 + 60)}%`,
                          }}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Pagination shimmer */}
        {showPagination && (
          <div className="flex w-full flex-shrink-0 items-center justify-between py-2">
            <Skeleton className="h-8 w-32" /> {/* Page info */}
            <div className="flex items-center gap-2">
              <Skeleton className="h-8 w-8" /> {/* Prev button */}
              <div className="flex gap-1">
                {Array.from({ length: 3 }).map((_, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Skeleton key={i} className="h-8 w-8" />
                ))}
              </div>
              <Skeleton className="h-8 w-8" /> {/* Next button */}
            </div>
            <Skeleton className="h-8 w-24" /> {/* Page size selector */}
          </div>
        )}
      </div>
    );
  },
);

EvalTableShimmer.displayName = "EvalTableShimmer";

export default EvalTableShimmer;
